/**
 * Features Tab
 * Displays extracted component features (ratings, specifications, types)
 */

import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip
} from '@mui/material';
import { 
  Settings as FeatureIcon
} from '@mui/icons-material';

interface ComponentFeature {
  reference?: string;
  type?: string;
  rating?: string;
  specifications?: Record<string, any>;
  page?: number;
  description?: string;
  feature?: string;
}

interface FeaturesTabProps {
  features: (ComponentFeature | string)[];
}

const FeaturesTab: React.FC<FeaturesTabProps> = ({ features }) => {
  if (!features || features.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          No component features available. Extract from a page first.
        </Alert>
      </Box>
    );
  }

  // Check if features are component objects or simple strings
  const isComponentFeatures = features.length > 0 && typeof features[0] === 'object' && 'reference' in features[0];

  if (!isComponentFeatures) {
    // Handle legacy string-based features
    return (
      <Box>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FeatureIcon color="primary" />
          <Typography variant="h6">
            Features & Notes ({features.length})
          </Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ p: 1, borderBottom: index < features.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                <Typography variant="body2">
                  {typeof feature === 'string' ? feature : JSON.stringify(feature)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  // Display component features as a table
  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FeatureIcon color="primary" />
        <Typography variant="h6">
          Component Features & Specifications ({features.length})
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reference</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Component Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Rating</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Specifications</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Page</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((feature, index) => {
              const comp = feature as ComponentFeature;
              return (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {comp.reference || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={comp.type || 'Unknown'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={comp.rating ? 'text.primary' : 'text.secondary'}>
                      {comp.rating || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {comp.specifications && Object.keys(comp.specifications).length > 0 ? (
                        Object.entries(comp.specifications).map(([key, value], idx) => (
                          <Chip
                            key={idx}
                            label={`${key}: ${value}`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No specs
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`Page ${comp.page || 'N/A'}`} 
                      size="small" 
                      variant="filled"
                      color="default"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Component Features:</strong> Detailed specifications extracted from the BOM including ratings, 
          voltage, current, sensitivity, and other technical specifications for each component.
        </Typography>
      </Alert>
    </Box>
  );
};

export default FeaturesTab;
